<template>
  <div class="c-field-mapping-tab">
    <div v-if="hasMultipleCRMs" class="c-field-mapping-tab__preferred-crm">
      <BittsSwitch
        v-model="isToggled"
        label="Use this CRM for field mapping"
        description="Map CRM fields from this data source to Crossbeam fields"
        @change="showConfirmModal = true"
      />
      <BittsDivider class="my-20" />
    </div>

    <FieldsSection
      ref="fieldsSectionRef"
      :is-disabled="hasMultipleCRMs && !isToggled"
    />

    <BittsModal
      v-if="showConfirmModal"
      class="c-field-mapping-tab__modal"
      variant="confirm"
      :title="modalTitle"
      description="This will update which CRM fields power your sales pipeline, filters, and more"
      @closed="handleModalCancel"
      @action="handleModalConfirm"
      :primary-button-text="confirmModalButtonText"
    />
  </div>
</template>

<script setup lang="ts">
import { BittsDivider, BittsModal, BittsSwitch } from '@crossbeam/bitts';

import { computed, ref, useTemplateRef, watch } from 'vue';
import { useRouter } from 'vue-router';

import useBusinessSettings from '@/composables/useBusinessSettings';
import useDataSourceSettings from '@/composables/useDataSourceSettings';
import { captureException } from '@/errors';
import { useFeedsStore, useFlashesStore } from '@/stores';
import { useRootStore } from '@/stores/RootStore';

import { DataSourceSettingsTabType } from '../../constants';

import FieldsSection from './FieldsSection.vue';

type Props = {
  feedId: number;
  // sourceId?: number; // Not used yet
};

const { feedId } = defineProps<Props>();

const router = useRouter();
const flashesStore = useFlashesStore();
const feedsStore = useFeedsStore();
const { updateOrgInfo } = useRootStore();
const { resetAll } = useBusinessSettings();
const { isPreferredCRMFeed, feed } = useDataSourceSettings();

const fieldsSectionRef =
  useTemplateRef<InstanceType<typeof FieldsSection>>('fieldsSectionRef');
const isToggled = ref<boolean>(false);
const showConfirmModal = ref<boolean>(false);

const hasMultipleCRMs = computed(() => feedsStore.hasMultipleCRMs);

const fallbackCRMFeed = computed(() => {
  const fallbackFeed = feedsStore.crmFeeds.find((f) => f.id !== feedId);
  return {
    id: fallbackFeed?.id,
    friendlyName: fallbackFeed?.integration?.friendly_name,
  };
});

const modalTitle = computed(() =>
  isToggled.value
    ? 'You’re about to switch the CRM used to map fields to Crossbeam'
    : `You’re about to switch to ${fallbackCRMFeed.value?.friendlyName} to map fields to Crossbeam`,
);
const confirmModalButtonText = computed(() =>
  isToggled.value
    ? 'Use this CRM'
    : `Use ${fallbackCRMFeed.value?.friendlyName}`,
);

function handleModalCancel() {
  isToggled.value = !isToggled.value;
  showConfirmModal.value = false;
}

async function handleModalConfirm() {
  // Feed id from the first other crm tab to replace the current one if un-toggled
  const fallbackFeedId = fallbackCRMFeed.value?.id;
  const newFeedId = isToggled.value ? feedId : fallbackFeedId;

  if (!newFeedId) {
    captureException(new Error('No feed id found'));
    flashesStore.addErrorFlash({
      message: 'Could not save field mapping',
      description: 'If the error persists contact support@crossbeam.com',
    });
    return;
  }

  try {
    await updateOrgInfo(newFeedId);
    resetAll();
    flashesStore.addSuccessFlash({
      message: 'Field mapping settings saved',
    });
    showConfirmModal.value = false;
    if (!isToggled.value) {
      router.push({
        name: 'data-sources-settings-new',
        params: { id: newFeedId, tab: DataSourceSettingsTabType.FIELD_MAPPING },
      });
    }
  } catch (error) {
    captureException(error);
    flashesStore.addErrorFlash({
      message: 'Could not save field mapping',
      description: 'If the error persists contact support@crossbeam.com',
    });
  }
}

// Initialize state when feed is loaded
watch(
  [feed],
  () => {
    if (feed.value) isToggled.value = isPreferredCRMFeed.value;
  },
  { immediate: true },
);

defineExpose({
  haveSettingsChanged: computed(
    () => fieldsSectionRef.value?.haveSettingsChanged ?? false,
  ),
});
</script>

<style lang="pcss" scoped>
@reference "../../../../../css/theme.css";
.c-field-mapping-tab {
  @apply flex flex-col;
}

.c-field-mapping-tab__modal {
  &.o-bitts-modal.bitts-modal--confirm {
    &:deep(dialog) {
      @apply w-[441px];
    }
    &:deep(.o-bitts-modal__header) {
      @apply py-40 px-24;
    }
    :deep(.o-bitts-modal__title),
    :deep(.o-bitts-modal__description) {
      @apply text-center  m-0;
    }
  }
}
</style>
